import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, STATUS_OPTIONS } from "@/lib/rn3";
import { toast } from "sonner";
import { LogIn, Upload, FileText, Calendar, MessageSquare, Download, BookOpen, ArrowRight, Shield, ShieldCheck, Lock, KeyRound } from "lucide-react";
import { useT, useLang } from "@/lib/i18n";
import TwoFactorSettings from "@/components/TwoFactorSettings";
import SessionCard from "@/components/SessionCard";
import NewBadge from "@/components/NewBadge";
import PasswordInput from "@/components/PasswordInput";
import { usePolling } from "@/lib/usePolling";
import SEO from "@/components/SEO";

const AuthSection = ({ onSuccess }) => {
  const t = useT();
  const { login, verify2fa, resend2fa, useRecoveryCode: submitRecoveryCode } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);

  // 2FA challenge state — populated only when /auth/login returns requires_2fa
  const [challenge, setChallenge] = useState(null); // { challenge_token, channel, destination_masked, expires_at, user_email }
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const result = await login(form.email, form.password);
      if (result?.requires_2fa) {
        setChallenge(result);
        toast.info(`${t("twofa.toast_sent")} ${result.destination_masked}`);
      } else {
        toast.success(t("auth.welcome_back"));
        onSuccess();
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : t("portal.dashboard.auth_failed");
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const submit2fa = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (useRecovery) {
        await submitRecoveryCode(challenge.challenge_token, recoveryCode.trim());
      } else {
        await verify2fa(challenge.challenge_token, code.trim());
      }
      toast.success(t("auth.welcome_back"));
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("twofa.toast_invalid"));
    } finally { setBusy(false); }
  };

  const resend = async () => {
    if (!challenge) return;
    setBusy(true);
    try {
      const r = await resend2fa(challenge.challenge_token);
      toast.success(`${t("twofa.toast_new_sent")} ${r.destination_masked}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("twofa.toast_resend_failed"));
    } finally { setBusy(false); }
  };

  // --- 2FA challenge screen ---
  if (challenge) {
    return (
      <div className="rn3-container py-20" data-testid="portal-2fa">
        <div className="max-w-md mx-auto">
          <div className="rn3-eyebrow text-center">{t("twofa.eyebrow")}</div>
          <h1 className="font-serif-display text-4xl mt-3 text-center">{t("twofa.confirm_title")}</h1>
          <div className="border-t border-[#C4A252] w-12 mx-auto my-5" />
          <p className="text-sm text-[#404040] text-center mb-6">
            {t("twofa.sent_to_a")} <b>{challenge.destination_masked}</b> {challenge.channel === "sms" ? t("twofa.sent_to_via_sms") : t("twofa.sent_to_via_email")}
          </p>
          <form onSubmit={submit2fa} className="space-y-5" data-testid="twofa-form">
            {!useRecovery ? (
              <div>
                <label className="rn3-eyebrow block mb-1">{t("twofa.code_label")}</label>
                <input
                  required
                  autoFocus
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  className="rn3-input text-center tracking-[0.5em] font-mono text-2xl"
                  data-testid="twofa-code"
                  placeholder="••••••"
                />
              </div>
            ) : (
              <div>
                <label className="rn3-eyebrow block mb-1">{t("twofa.recovery_label")}</label>
                <input
                  required
                  autoFocus
                  value={recoveryCode}
                  onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
                  className="rn3-input font-mono"
                  data-testid="twofa-recovery"
                  placeholder={t("twofa.recovery_placeholder")}
                />
              </div>
            )}
            <button disabled={busy} type="submit" className="rn3-btn-primary w-full justify-center" data-testid="twofa-submit">
              {busy ? "…" : (useRecovery ? t("twofa.use_recovery_btn") : t("twofa.verify_btn"))}
            </button>
          </form>
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3 text-xs">
            {!useRecovery ? (
              <button type="button" onClick={resend} disabled={busy} className="text-[#8B1C22] underline" data-testid="twofa-resend">{t("twofa.resend")}</button>
            ) : (
              <button type="button" onClick={() => { setUseRecovery(false); setRecoveryCode(""); }} className="text-[#737373] underline" data-testid="twofa-back-to-code">{t("twofa.back_to_code")}</button>
            )}
            {!useRecovery && (
              <button type="button" onClick={() => setUseRecovery(true)} className="text-[#737373] underline" data-testid="twofa-use-recovery">{t("twofa.use_recovery_link")}</button>
            )}
            <button type="button" onClick={() => { setChallenge(null); setCode(""); setRecoveryCode(""); setUseRecovery(false); }} className="text-[#737373] underline" data-testid="twofa-cancel">
              {t("twofa.cancel")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rn3-container py-20" data-testid="portal-auth">
      <SEO
        title="Admin Portal — Sign In | Roman Numerals III"
        description="Secure sign-in for Roman Numerals III staff and invited clients. This portal is invite-only — new clients receive a personalized set-up link from Adelis after onboarding."
        path="/portal"
        noindex
      />
      <div className="max-w-md mx-auto">
        <div className="rn3-eyebrow text-center">{t("portal.eyebrow")}</div>
        <h1 className="font-serif-display text-4xl mt-3 text-center">
          {t("auth.welcome_back")}
        </h1>
        <div className="border-t border-[#C4A252] w-12 mx-auto my-5" />

        <form onSubmit={submit} className="space-y-5" data-testid="auth-form">
          <div>
            <label className="rn3-eyebrow block mb-1">{t("common.email")}</label>
            <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rn3-input" data-testid="auth-email" />
          </div>
          <div>
            <label className="rn3-eyebrow block mb-1">{t("auth.password")}</label>
            <PasswordInput required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="auth-password" />
            <div className="text-right mt-2">
              <Link to="/portal/forgot" className="text-xs text-[#8B1C22] underline" data-testid="forgot-link">{t("auth.forgot_password")}</Link>
            </div>
          </div>
          <button disabled={busy} type="submit" className="rn3-btn-primary w-full justify-center" data-testid="auth-submit">
            <LogIn className="w-4 h-4" /> {busy ? "…" : t("nav.sign_in")}
          </button>
        </form>

        {/* Invite-only notice — replaces the old public "Sign Up" tab.
            New clients must be onboarded by an admin via the Leads → Invite
            flow, which emails them a personalized "Set your password" link. */}
        <div className="mt-6 border border-[#E5E5E5] bg-[#FDFBF7] px-5 py-4 text-center" data-testid="invite-only-notice">
          <div className="rn3-eyebrow text-[#8B1C22] mb-1.5">{t("portal.invite_only.heading", "Invite-only access")}</div>
          <p className="text-xs text-[#404040] leading-relaxed">
            {t(
              "portal.invite_only.body",
              "New clients are onboarded by Adelis directly. Complete your intake or book a consultation, and you'll receive a personalized set-up link by email.",
            )}
          </p>
          <div className="mt-3 flex gap-3 justify-center text-xs">
            <Link to="/intake" className="text-[#8B1C22] underline" data-testid="invite-cta-intake">
              {t("portal.invite_only.cta_intake", "Start intake")}
            </Link>
            <span className="text-[#C4A252]">·</span>
            <Link to="/appointments" className="text-[#8B1C22] underline" data-testid="invite-cta-book">
              {t("portal.invite_only.cta_book", "Book a call")}
            </Link>
          </div>
        </div>

        {/* Security assurance strip — quietly reinforces the trust story before
            returning clients even type their password. Mirrors the intake
            confirmation badge so the whole funnel speaks with one voice. */}
        <div className="mt-8 border-t border-[#E5E5E5] pt-5" data-testid="portal-security-strip">
          <div className="rn3-eyebrow flex items-center justify-center gap-2 text-[#8B1C22]">
            <Shield className="w-3.5 h-3.5" /> {t("portal.security_strip.heading")}
          </div>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
            <div className="flex flex-col items-center gap-1.5" data-testid="portal-security-bcrypt">
              <KeyRound className="w-4 h-4 text-[#C4A252]" />
              <span className="text-[10px] uppercase tracking-widest text-[#0A0A0A]">{t("portal.security_strip.bcrypt")}</span>
            </div>
            <div className="flex flex-col items-center gap-1.5" data-testid="portal-security-2fa">
              <Lock className="w-4 h-4 text-[#C4A252]" />
              <span className="text-[10px] uppercase tracking-widest text-[#0A0A0A]">{t("portal.security_strip.two_fa")}</span>
            </div>
            <div className="flex flex-col items-center gap-1.5" data-testid="portal-security-transit">
              <ShieldCheck className="w-4 h-4 text-[#C4A252]" />
              <span className="text-[10px] uppercase tracking-widest text-[#0A0A0A]">{t("portal.security_strip.encrypted_transit")}</span>
            </div>
            <div className="flex flex-col items-center gap-1.5" data-testid="portal-security-audit">
              <FileText className="w-4 h-4 text-[#C4A252]" />
              <span className="text-[10px] uppercase tracking-widest text-[#0A0A0A]">{t("portal.security_strip.audit_log")}</span>
            </div>
          </div>
          <p className="mt-4 text-[11px] text-[#737373] text-center max-w-sm mx-auto">
            {t("portal.security_strip.tagline")}
          </p>
        </div>
      </div>
    </div>
  );
};

const ClientDashboard = () => {
  const t = useT();
  const { lang } = useLang();
  const nav = useNavigate();
  const [intakes, setIntakes] = useState([]);
  const [appts, setAppts] = useState([]);
  const [docs, setDocs] = useState([]);
  const [messages, setMessages] = useState([]);
  const [msgBody, setMsgBody] = useState("");
  const [files, setFiles] = useState([]);
  const [books, setBooks] = useState([]);

  const load = async () => {
    try {
      const [i, a, d, m, b] = await Promise.all([
        api.get("/intakes"), api.get("/appointments"), api.get("/documents"), api.get("/messages"), api.get("/portal/books"),
      ]);
      setIntakes(i.data); setAppts(a.data); setDocs(d.data); setMessages(m.data); setBooks(b.data);
    } catch { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  // Auto-refresh portal data every 15s so admin replies, shared documents,
  // and intake updates appear without manual reload.
  usePolling(load, 15000);

  // Curated bookkeeping metadata for client uploads. Categories are friendly
  // labels (bilingual via i18n keys `portal.upload.cat.*`); "other" opens a
  // free-text field so admin can amend on the admin side.
  const CATEGORIES = [
    "office_supplies", "meals_entertainment", "fuel_auto", "travel_lodging",
    "utilities", "software_subscriptions", "rent_lease", "insurance",
    "marketing_advertising", "professional_services", "bank_card_fees",
    "cogs", "other",
  ];
  const DOC_TYPES = ["receipt", "bank_statement", "invoice", "check", "other"];
  const [meta, setMeta] = useState({
    account_label: "", period: "", doc_type: "receipt", vendor: "",
    amount: "", category_key: "office_supplies", category_other: "",
    subcategory: "", notes: "",
  });
  const setField = (k, v) => setMeta((m) => ({ ...m, [k]: v }));

  // Build last-12-months list for the period dropdown
  // Period options — go back 3 full years (36 months) so historical receipts
  // (e.g. 2023 tax-year documents uploaded in 2026) can still be tagged with
  // the correct accounting period. Grouped by year using <optgroup> so long
  // lists stay easy to scan.
  const periodOptions = (() => {
    const groups = new Map(); // year → [{value,label}]
    const now = new Date();
    for (let i = 0; i < 36; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const year = d.getFullYear();
      const val = `${year}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const label = d.toLocaleString(lang === "es" ? "es-US" : "en-US", { month: "long", year: "numeric" });
      const arr = groups.get(year) || [];
      arr.push({ value: val, label: label.charAt(0).toUpperCase() + label.slice(1) });
      groups.set(year, arr);
    }
    return Array.from(groups.entries()); // [[year, months[]], ...] newest→oldest
  })();

  // Autocomplete for account_label — pull unique labels from past uploads
  const accountSuggestions = Array.from(new Set(docs.map((d) => d.account_label).filter(Boolean))).slice(0, 8);

  const upload = async () => {
    if (!files.length) return;
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("category", "portal");
        if (meta.account_label) fd.append("account_label", meta.account_label);
        if (meta.period) fd.append("period", meta.period);
        if (meta.doc_type) fd.append("doc_type", meta.doc_type);
        if (meta.vendor) fd.append("vendor", meta.vendor);
        if (meta.amount) fd.append("amount", meta.amount);
        if (meta.category_key) fd.append("category_key", meta.category_key);
        if (meta.category_key === "other" && meta.category_other) fd.append("category_other", meta.category_other);
        if (meta.subcategory) fd.append("subcategory", meta.subcategory);
        if (meta.notes) fd.append("notes", meta.notes);
        await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(t("portal.dashboard.uploaded"));
      setFiles([]);
      setMeta((m) => ({ ...m, vendor: "", amount: "", category_other: "", subcategory: "", notes: "" })); // keep account/period/category defaults, clear per-file fields
      load();
    } catch { toast.error(t("portal.dashboard.upload_failed")); }
  };

  const sendMsg = async (e) => {
    e.preventDefault();
    if (!msgBody.trim()) return;
    await api.post("/messages", { body: msgBody, to_admin: true });
    setMsgBody("");
    toast.success(t("portal.dashboard.msg_sent"));
    load();
  };

  // Authenticated client-side document download (the GET endpoint requires a
  // JWT in the Authorization header — plain <a target="_blank"> can't send it).
  const downloadDoc = async (doc) => {
    try {
      const res = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename || `document-${doc.id}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error(t("portal.dashboard.upload_failed"));
    }
  };

  return (
    <div className="rn3-container py-12" data-testid="portal-dashboard">
      <div className="rn3-eyebrow">{t("portal.eyebrow")}</div>
      <h1 className="font-serif-display text-4xl mt-3">{t("portal.dashboard.title")}</h1>
      <div className="border-t border-[#C4A252] w-12 mt-4 mb-10" />

      {books.length > 0 && (
        <button
          onClick={() => nav("/portal/books")}
          className="w-full text-left rn3-card mb-8 group hover:border-[#8B1C22] transition-colors"
          data-testid="open-my-books"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#8B1C22] text-white flex items-center justify-center shrink-0">
              <BookOpen className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <div className="rn3-eyebrow">{t("portal.books.eyebrow")}</div>
              <div className="font-serif-display text-xl mt-1">
                {books.length === 1 ? `${books[0].name} · ${t("portal.books.headline_one")}` : `${books.length} ${t("portal.books.headline_many")}`}
              </div>
              <div className="text-xs text-[#737373] mt-1">{t("portal.books.sub")}</div>
            </div>
            <ArrowRight className="w-5 h-5 text-[#737373] group-hover:text-[#8B1C22] group-hover:translate-x-1 transition-all" />
          </div>
        </button>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <section className="rn3-card lg:col-span-1" data-testid="my-intakes">
          <div className="flex items-center gap-2 rn3-eyebrow"><FileText className="w-4 h-4" /> {t("portal.dashboard.intakes")}</div>
          <div className="mt-4 space-y-3">
            {intakes.length === 0 && <div className="text-sm text-[#737373]">{t("portal.dashboard.no_intakes")}</div>}
            {intakes.map((i) => (
              <div key={i.id} className="border-b border-[#E5E5E5] pb-3" data-testid={`intake-row-${i.id}`}>
                <div className="font-medium">{i.service.replace(/_/g, " ")}</div>
                <div className="text-xs text-[#737373]">{new Date(i.created_at).toLocaleString()}</div>
                <div className="text-xs mt-1"><span className="px-2 py-0.5 bg-[#FDFBF7] border border-[#C4A252] text-[#8B1C22]">{STATUS_OPTIONS.find(s => s.key === i.status)?.label || i.status}</span></div>
              </div>
            ))}
          </div>
        </section>

        <section className="rn3-card lg:col-span-1" data-testid="my-appts">
          <div className="flex items-center gap-2 rn3-eyebrow"><Calendar className="w-4 h-4" /> {t("portal.dashboard.appts")}</div>
          <div className="mt-4 space-y-3">
            {appts.length === 0 && <div className="text-sm text-[#737373]">{t("portal.dashboard.no_appts")}</div>}
            {appts.map((a) => (
              <div key={a.id} className="border-b border-[#E5E5E5] pb-3">
                <div className="font-medium">{a.service.replace(/_/g, " ")}</div>
                <div className="text-xs text-[#737373]">{a.date} · {a.time}</div>
                <div className="text-xs mt-1"><span className="px-2 py-0.5 bg-[#FDFBF7] border border-[#C4A252] text-[#8B1C22]">{a.status}</span></div>
              </div>
            ))}
          </div>
        </section>

        <section className="rn3-card lg:col-span-1" data-testid="my-docs">
          <div className="flex items-center gap-2 rn3-eyebrow"><Upload className="w-4 h-4" /> {t("portal.dashboard.documents")}</div>

          {/* Structured upload form — helps admin reconcile faster */}
          <div className="mt-4 space-y-3 text-sm">
            <div>
              <label className="text-xs font-medium text-[#0A0A0A] mb-1 block">{t("portal.upload.file_label") || "File(s)"}</label>
              <input type="file" multiple onChange={(e) => setFiles(Array.from(e.target.files || []))} className="block w-full text-xs file:mr-3 file:py-1.5 file:px-3 file:border-0 file:bg-[#0A0A0A] file:text-white file:text-xs file:uppercase file:tracking-widest hover:file:bg-[#8B1C22] file:cursor-pointer cursor-pointer" data-testid="portal-file-input" />
              {files.length > 0 && <div className="text-[11px] text-[#737373] mt-1">{files.length} {t("portal.upload.selected") || "selected"}</div>}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium text-[#0A0A0A] mb-1 block">{t("portal.upload.account_label") || "Account"}</label>
                <input
                  type="text"
                  value={meta.account_label}
                  onChange={(e) => setField("account_label", e.target.value)}
                  placeholder={t("portal.upload.account_placeholder") || "Chase Checking"}
                  list="portal-account-suggestions"
                  className="rn3-input w-full text-xs"
                  data-testid="portal-upload-account"
                />
                <datalist id="portal-account-suggestions">
                  {accountSuggestions.map((a) => <option key={a} value={a} />)}
                </datalist>
              </div>
              <div>
                <label className="text-xs font-medium text-[#0A0A0A] mb-1 block">{t("portal.upload.period_label") || "Period"}</label>
                <select value={meta.period} onChange={(e) => setField("period", e.target.value)} className="rn3-input w-full text-xs" data-testid="portal-upload-period">
                  <option value="">{t("portal.upload.period_none") || "— any —"}</option>
                  {periodOptions.map(([year, months]) => (
                    <optgroup key={year} label={String(year)}>
                      {months.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </optgroup>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-[#0A0A0A] mb-1 block">{t("portal.upload.type_label") || "Type"}</label>
              <div className="grid grid-cols-5 gap-1">
                {DOC_TYPES.map((dt) => (
                  <button
                    type="button"
                    key={dt}
                    onClick={() => setField("doc_type", dt)}
                    className={`text-[10px] uppercase tracking-widest py-1.5 border ${meta.doc_type === dt ? "bg-[#8B1C22] text-white border-[#8B1C22]" : "bg-white text-[#0A0A0A] border-[#E5E5E5] hover:border-[#8B1C22]"}`}
                    data-testid={`portal-upload-type-${dt}`}
                  >
                    {t(`portal.upload.type.${dt}`) || dt.replace("_", " ")}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium text-[#0A0A0A] mb-1 block">{t("portal.upload.vendor_label") || "Vendor"}</label>
                <input
                  type="text"
                  value={meta.vendor}
                  onChange={(e) => setField("vendor", e.target.value)}
                  placeholder={t("portal.upload.vendor_placeholder") || "Home Depot"}
                  className="rn3-input w-full text-xs"
                  data-testid="portal-upload-vendor"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-[#0A0A0A] mb-1 block">{t("portal.upload.amount_label") || "Amount ($)"}</label>
                <input
                  type="number"
                  step="0.01"
                  value={meta.amount}
                  onChange={(e) => setField("amount", e.target.value)}
                  placeholder="0.00"
                  className="rn3-input w-full text-xs"
                  data-testid="portal-upload-amount"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-[#0A0A0A] mb-1 block">{t("portal.upload.category_label") || "Category"}</label>
              <select
                value={meta.category_key}
                onChange={(e) => setField("category_key", e.target.value)}
                className="rn3-input w-full text-xs"
                data-testid="portal-upload-category"
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{t(`portal.upload.cat.${c}`) || c.replace(/_/g, " ")}</option>
                ))}
              </select>
              {meta.category_key === "other" && (
                <input
                  type="text"
                  value={meta.category_other}
                  onChange={(e) => setField("category_other", e.target.value)}
                  placeholder={t("portal.upload.category_other_placeholder") || "Describe the category…"}
                  className="rn3-input w-full text-xs mt-2"
                  data-testid="portal-upload-category-other"
                />
              )}
            </div>

            <div>
              <label className="text-xs font-medium text-[#0A0A0A] mb-1 block">{t("portal.upload.subcategory_label") || "Subcategory (optional)"}</label>
              <input
                type="text"
                value={meta.subcategory}
                onChange={(e) => setField("subcategory", e.target.value)}
                placeholder={t("portal.upload.subcategory_placeholder") || "e.g. Gas, Oil change, Client meal…"}
                list="portal-subcategory-suggestions"
                className="rn3-input w-full text-xs"
                data-testid="portal-upload-subcategory"
              />
              <datalist id="portal-subcategory-suggestions">
                {Array.from(new Set(docs.map((d) => d.subcategory).filter(Boolean))).slice(0, 20).map((s) => <option key={s} value={s} />)}
              </datalist>
            </div>

            <div>
              <label className="text-xs font-medium text-[#0A0A0A] mb-1 block">{t("portal.upload.notes_label") || "Notes (optional)"}</label>
              <textarea
                rows={2}
                value={meta.notes}
                onChange={(e) => setField("notes", e.target.value)}
                placeholder={t("portal.upload.notes_placeholder") || "Anything else useful — e.g. \"Split with card ending 1234\""}
                className="rn3-input w-full text-xs"
                data-testid="portal-upload-notes"
              />
            </div>

            <button onClick={upload} disabled={!files.length} className="rn3-btn-gold w-full justify-center disabled:opacity-40 disabled:cursor-not-allowed" data-testid="portal-upload-btn">
              <Upload className="w-3.5 h-3.5" /> {t("portal.dashboard.upload")}
            </button>
          </div>

          <div className="mt-6 space-y-2 max-h-56 overflow-y-auto border-t border-[#E5E5E5] pt-3">
            {docs.map((d) => (
              <div key={d.id} className="flex items-center justify-between text-sm border-b border-[#E5E5E5] pb-2">
                <div className="truncate flex items-center min-w-0">
                  <span className="truncate">{d.filename}</span>
                  <NewBadge createdAt={d.created_at} />
                </div>
                <button onClick={() => downloadDoc(d)} className="text-[#8B1C22] inline-flex items-center gap-1 hover:underline cursor-pointer" data-testid={`portal-doc-download-${d.id}`} aria-label={`Download ${d.filename}`}><Download className="w-3 h-3" /></button>
              </div>
            ))}
          </div>
        </section>

        <section className="rn3-card lg:col-span-3" data-testid="portal-messages">
          <div className="flex items-center gap-2 rn3-eyebrow"><MessageSquare className="w-4 h-4" /> {t("portal.dashboard.messages")}</div>
          <form onSubmit={sendMsg} className="mt-4 flex gap-3">
            <input value={msgBody} onChange={(e) => setMsgBody(e.target.value)} className="rn3-input" placeholder={t("portal.dashboard.write_message")} data-testid="msg-input" />
            <button className="rn3-btn-primary" data-testid="msg-send">{t("portal.dashboard.send")}</button>
          </form>
          <div className="mt-5 space-y-3 max-h-72 overflow-y-auto">
            {messages.length === 0 && <div className="text-sm text-[#737373]">{t("portal.dashboard.no_messages")}</div>}
            {messages.map((m) => (
              <div key={m.id} className={`p-3 border ${m.from_admin ? "bg-[#FDFBF7] border-[#C4A252]" : "border-[#E5E5E5]"}`}>
                <div className="text-xs text-[#737373] flex items-center">
                  <span>{m.from_admin ? "Roman Numerals III" : t("portal.dashboard.you")} · {new Date(m.created_at).toLocaleString()}</span>
                  {m.from_admin && <NewBadge createdAt={m.created_at} />}
                </div>
                <div className="text-sm mt-1 whitespace-pre-wrap">{m.body}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Account Security */}
        <section className="lg:col-span-3" data-testid="portal-security">
          <div className="rn3-eyebrow flex items-center gap-2 mb-3"><Shield className="w-4 h-4" /> {t("portal.security.heading")}</div>
          <div className="grid lg:grid-cols-2 gap-6">
            <TwoFactorSettings />
            <ClientActivityFeed />
            <SessionCard className="lg:col-span-2" />
          </div>
        </section>
      </div>
    </div>
  );
};

const ClientActivityFeed = () => {
  const t = useT();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/users/me/activity?days=30&limit=15")
      .then(({ data }) => setItems(data.items || []))
      .finally(() => setLoading(false));
  }, []);
  return (
    <div className="rn3-card" data-testid="my-activity">
      <div className="rn3-eyebrow">{t("portal.activity.eyebrow")}</div>
      <h3 className="font-serif-display text-2xl mt-2">{t("portal.activity.title")}</h3>
      <div className="border-t border-[#C4A252] w-10 mt-3 mb-4" />
      {loading ? (
        <div className="text-sm text-[#737373]">{t("portal.activity.loading")}</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-[#737373]">{t("portal.activity.empty")}</div>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="my-activity-list">
          {items.map((e) => (
            <div key={e.id} className="border border-[#E5E5E5] p-3 text-xs">
              <div className="flex items-center justify-between gap-2">
                <span className={`px-2 py-0.5 text-[10px] uppercase tracking-widest border ${e.success ? "border-green-300 text-green-700 bg-green-50" : "border-red-300 text-red-700 bg-red-50"}`}>{e.action}</span>
                <span className="text-[#737373]">{new Date(e.timestamp).toLocaleString()}</span>
              </div>
              <div className="mt-1 text-[#404040] flex flex-wrap gap-x-3">
                <span>{t("portal.activity.ip_label")} <code className="font-mono">{e.ip_address || "—"}</code></span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Portal = () => {
  const t = useT();
  const { user, loading } = useAuth();
  const nav = useNavigate();
  useEffect(() => {
    if (!loading && user && user.role === "admin") nav("/admin", { replace: true });
  }, [user, loading, nav]);
  if (loading) return <div className="rn3-container py-20 text-sm text-[#737373]">{t("portal.dashboard.loading")}</div>;
  if (!user) return <AuthSection onSuccess={() => { /* re-render */ }} />;
  if (user.role === "admin") return null;
  return <ClientDashboard />;
};

export default Portal;
